import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { PageContent } from '@/types';
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { pageContentService } from '@/services/storage';
import AIChatWidget from '@/components/AIChatWidget';

const navItems = [
  { path: '/', label: '首页' },
  {
    path: '/about',
    label: '关于瀚昕微',
    children: [
      { path: '/about', label: '公司简介' },
      { path: '/about?tab=philosophy', label: '经营理念' },
      { path: '/about?tab=honors', label: '荣誉资质' },
      { path: '/news', label: '公司新闻' },
      { path: '/contact', label: '联系我们' },
    ],
  },
  { path: '/products', label: '产品中心' },
  { path: '/applications', label: '应用领域' },
  {
    path: '/technology',
    label: '技术与支持',
    children: [
      { path: '/technology', label: '质量体系' },
      { path: '/sample-apply', label: '样品申请与购买' },
    ],
  },
  { path: '/jobs', label: '加入我们' },
];

export default function FrontLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logo, setLogo] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const [pageContent, setPageContent] = useState<PageContent>({} as PageContent);

  useEffect(() => {
    pageContentService.get().then((content) => {
      setLogo(content.logo || '');
      setPageContent(content);
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              {logo ? (
                <img src={logo} alt="Logo" className="h-14 md:h-16 w-auto object-contain" />
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-2xl md:text-3xl">H</span>
                  </div>
                  <div className="hidden sm:block">
                    <div className={`font-bold text-xl md:text-2xl leading-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                      瀚昕微电子
                    </div>
                    <div className={`text-sm md:text-base ${scrolled ? 'text-gray-500' : 'text-white/70'}`}>
                      Hypower Microelectronics
                    </div>
                  </div>
                </div>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) =>
                item.children ? (
                  <DropdownMenu key={item.path}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`px-4 py-2 text-base font-medium flex items-center transition-colors ${
                          scrolled
                            ? 'text-gray-700 hover:text-blue-600'
                            : 'text-blue-900 hover:text-blue-700'
                        }`}
                      >
                        {item.label}
                        <ChevronDown className="ml-1 h-5 w-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.path} asChild>
                          <Link to={child.path} className="w-full text-base">
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 text-base font-medium transition-colors ${
                      location.pathname === item.path
                        ? scrolled
                          ? 'text-blue-600'
                          : 'text-blue-900'
                        : scrolled
                        ? 'text-gray-700 hover:text-blue-600'
                        : 'text-blue-900 hover:text-blue-700'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </nav>

            {/* Language & Admin */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/admin"
                className={`text-sm ${scrolled ? 'text-gray-600 hover:text-blue-600' : 'text-white/70 hover:text-white'}`}
              >
                EN
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className={`h-6 w-6 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
              ) : (
                <Menu className={`h-6 w-6 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <nav className="container mx-auto px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <div key={item.path}>
                  <Link
                    to={item.path}
                    className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="pl-4 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block py-1 text-sm text-gray-500 hover:text-blue-600"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16 md:h-20" />

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* AI Chat Widget */}
      <AIChatWidget />

      {/* Footer */}
      <footer
        className="text-white border-t border-slate-700"
        style={pageContent.footer?.backgroundImage ? {
          backgroundImage: `url(${pageContent.footer.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : { backgroundColor: '#1e293b' }}
      >
        {/* Links Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  {pageContent.footer?.companyLogo ? (
                    <img
                      src={pageContent.footer.companyLogo}
                      alt="Logo"
                      className="w-12 h-12 object-contain rounded-lg"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-xl">H</span>
                    </div>
                  )}
                  <div>
                    <div className="font-bold text-lg">瀚昕微电子</div>
                    <div className="text-sm text-gray-400">Hypower Microelectronics</div>
                  </div>
                </div>
                <p className="text-gray-400 text-base mb-6">
                  专注于高性能、高品质数模混合芯片研发的芯片设计公司
                </p>

                {/* WeChat QR Code */}
                <div className="mt-4">
                  <h4 className="font-bold mb-3 text-base">微信公众号</h4>
                  <div className="bg-white rounded-lg p-2 w-28 h-28 flex items-center justify-center overflow-hidden">
                    {pageContent.footer?.qrCode ? (
                      <img
                        src={pageContent.footer.qrCode}
                        alt="WeChat QR Code"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="text-center text-gray-400 text-xs">
                        <div className="w-20 h-20 bg-gray-200 rounded mb-1 flex items-center justify-center">
                          <span className="text-2xl">📱</span>
                        </div>
                        <span>扫码关注</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* About Links */}
              <div>
                <h4 className="font-bold mb-4 text-base">关于瀚昕微</h4>
                <ul className="space-y-3 text-base text-gray-400">
                  <li>
                    <Link to="/about" className="hover:text-white">
                      公司简介
                    </Link>
                  </li>
                  <li>
                    <Link to="/about?tab=philosophy" className="hover:text-white">
                      经营理念
                    </Link>
                  </li>
                  <li>
                    <Link to="/about?tab=honors" className="hover:text-white">
                      荣誉资质
                    </Link>
                  </li>
                  <li>
                    <Link to="/news" className="hover:text-white">
                      公司新闻
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="hover:text-white">
                      联系我们
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Product Links */}
              <div>
                <h4 className="font-bold mb-4 text-base">产品中心</h4>
                <ul className="space-y-3 text-base text-gray-400">
                  <li>
                    <Link to="/products?category=1" className="hover:text-white">
                      LDO线性稳压器
                    </Link>
                  </li>
                  <li>
                    <Link to="/products?category=2" className="hover:text-white">
                      过压过流保护IC
                    </Link>
                  </li>
                  <li>
                    <Link to="/products?category=3" className="hover:text-white">
                      线性充电IC
                    </Link>
                  </li>
                  <li>
                    <Link to="/products?category=4" className="hover:text-white">
                      DCDC功率转换器
                    </Link>
                  </li>
                  <li>
                    <Link to="/products?category=5" className="hover:text-white">
                      协议IC
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-bold mb-4 text-base">联系我们</h4>
                <ul className="space-y-3 text-base text-gray-400">
                  <li className="flex items-start space-x-2">
                    <Phone className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <div>售前咨询:</div>
                      <div>0510-8822 6558</div>
                      <div>021-6052 6680</div>
                      <div className="text-sm text-gray-500">周一至周五 9:00-18:00</div>
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <div>sales@hypwr.com</div>
                      <div>support@hypwr.com</div>
                      <div className="text-sm text-gray-500">1-2两个工作日回复</div>
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      无锡市锡山区二泉东路19号
                      <br />
                      集智商务广场16楼
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 py-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
              <div>
                版权所有 &copy; Copyright 2024 瀚昕微电子(无锡)有限公司
              </div>
              <div className="mt-2 md:mt-0" style={{ display: 'none' }}>
                <Link to="/admin" className="hover:text-gray-300">
                  管理后台
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
